import { createEffect, createMemo, createSignal, onCleanup, Show, type Component } from 'solid-js';

import { castClock, type GameStore } from '../state/store';
import { castBarView, portalBarView } from './castbar';

/**
 * The cast bar: the skill being cast — or the town portal opening — how far through its window, and
 * the seconds left.
 *
 * **Below the character, in the middle of the screen**, because that is where the player is
 * already looking while they cast — and the one band of the HUD nothing else claims: the action
 * bar and orbs are along the bottom, the loot prompt and drop notices just above them, the target
 * frame and town bar at the top. Measured in a browser (`elementFromPoint`), not assumed.
 *
 * **It never takes the pointer** — no `hud-interactive` — so a click on the world under it walks
 * or attacks exactly as it would with no bar there.
 *
 * Animated by `requestAnimationFrame` only while a cast is showing, then stopped: a bar that
 * advanced only when a snapshot arrived would step at 30 Hz.
 */
export const CastBar: Component<{ store: GameStore; clock?: () => number }> = (props) => {
  const clock = props.clock ?? castClock;
  const [now, setNow] = createSignal(clock());
  let frame = 0;

  createEffect(() => {
    if (!props.store.castBar()) return;
    const tick = () => {
      setNow(clock());
      if (props.store.castBar() && typeof requestAnimationFrame !== 'undefined') frame = requestAnimationFrame(tick);
    };
    tick();
    onCleanup(() => {
      if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(frame);
    });
  });

  // The opening's full length, taken from the first value it was seen with and forgotten when it
  // ends — so a second opening measures itself rather than the first.
  let portalTotal = 0;
  const portal = createMemo(() => {
    const remaining = props.store.self()?.portalCastSec;
    if (!(remaining !== undefined && remaining > 0)) {
      portalTotal = 0;
      return null;
    }
    if (remaining > portalTotal) portalTotal = remaining;
    return portalBarView(remaining, portalTotal);
  });
  // A portal opening outranks a skill cast: it is the one a hit takes away, and the one the player
  // is waiting on to leave.
  const view = createMemo(() => portal() ?? castBarView(props.store.castBar(), props.store.self()?.state, now()));

  return (
    <Show when={view()}>
      {(v) => (
        <div class="cast-bar" data-testid="cast-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v().progress * 100)} aria-label={`Casting ${v().name}`}>
          <div class="cast-bar__track">
            <div class="cast-bar__fill" data-testid="cast-bar-fill" style={{ width: `${(v().progress * 100).toFixed(1)}%` }} />
          </div>
          <div class="cast-bar__label">
            <span class="cast-bar__name">{v().name}</span>
            <span class="cast-bar__time" data-testid="cast-bar-time">{v().remaining.toFixed(1)}s</span>
          </div>
        </div>
      )}
    </Show>
  );
};
